import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Zap, TrendingUp, AlertCircle, CheckCircle, Layers, Globe, ArrowRight, Users, Target, Briefcase } from 'lucide-react';

const EmailAssistantProject = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('evolution');

  const evolutionStages = [
    {
      id: 'initial',
      name: 'Initial Version',
      icon: AlertCircle,
      color: 'from-orange-500 to-red-500',
      bgColor: 'from-orange-500/20 to-red-500/20',
      borderColor: 'border-orange-500/30',
      title: 'Simple LLM-Based Email Processing',
      description: 'Basic email assistance using language models',
      features: [
        'Simple LLM email responses',
        'Basic text understanding',
        'Template-based replies',
        'Limited context awareness',
        'No external system integration'
      ]
    },
    {
      id: 'rag-agent',
      name: 'RAG + Agent Enhancement',
      icon: Zap,
      color: 'from-blue-500 to-purple-500',
      bgColor: 'from-blue-500/20 to-purple-500/20',
      borderColor: 'border-blue-500/30',
      title: 'Knowledge-Augmented Agent System',
      description: 'Enhanced with retrieval and agent capabilities',
      features: [
        'RAG integration for knowledge base',
        'Multi-agent architecture',
        'Context-aware responses',
        'Document OCR processing',
        'Improved accuracy and relevance'
      ]
    },
    {
      id: 'mcp-integration',
      name: 'MCP Integration',
      icon: CheckCircle,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-500/30',
      title: 'Real-World Production Integration',
      description: 'Connected to actual systems via MCP',
      features: [
        'MCP (Model Control Protocol) integration',
        'Direct Outlook integration',
        'Real iComposer system connectivity',
        'Live claim processing',
        'Production-ready workflow'
      ]
    }
  ];

  const useCaseWorkflow = [
    {
      step: '01',
      title: 'Email Received in Outlook',
      icon: Mail,
      description: 'Customer submits claim-related email with attachments',
      details: [
        'Medical bills and documents attached',
        'Policy inquiry or claim request',
        'Insurance employee opens in Outlook'
      ]
    },
    {
      step: '02',
      title: 'AI Assistant Activated',
      icon: Zap,
      description: 'Email Assistant analyzes the request automatically',
      details: [
        'OCR extracts text from attachments',
        'Identifies claim type and context',
        'Retrieves relevant policy information'
      ]
    },
    {
      step: '03',
      title: 'iComposer Integration via MCP',
      icon: Globe,
      description: 'Agent connects to backend systems for real-time data',
      details: [
        'Query customer policy and coverage',
        'Medical bill matching via iComposer',
        'Cross-reference ATC medical codes',
        'Retrieve claim history and status'
      ]
    },
    {
      step: '04',
      title: 'Intelligent Response Generated',
      icon: CheckCircle,
      description: 'AI generates comprehensive response with recommendation',
      details: [
        'Claim eligibility assessment',
        'Coverage amount calculation',
        'Required additional documents list',
        'Next steps for customer'
      ]
    }
  ];

  const businessImpact = [
    {
      icon: TrendingUp,
      title: '75% Faster Processing',
      description: 'From hours/days to minutes for standard inquiries',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Users,
      title: '300% Productivity Boost',
      description: 'Emails processed per staff member',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Target,
      title: '90% Error Reduction',
      description: 'Automated validation ensures accuracy',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  const renderEvolutionContent = () => (
    <div className="space-y-6">
      {evolutionStages.map((stage, index) => {
        const Icon = stage.icon;
        return (
          <motion.div
            key={stage.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-gradient-to-br ${stage.bgColor} rounded-xl border ${stage.borderColor} p-6`}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className={`bg-gradient-to-br ${stage.color} p-3 rounded-lg`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-lg text-white mb-1">{stage.name}</h4>
                <p className="text-sm text-gray-400 mb-3">{stage.title}</p>
                <p className="text-sm text-gray-300">{stage.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {stage.features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-white/5 rounded-lg p-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  const renderUseCaseContent = () => (
    <div className="space-y-6">
      {/* Use Case Introduction */}
      <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
        <div className="flex items-center gap-3 mb-4">
          <Briefcase className="w-6 h-6 text-green-400" />
          <h4 className="text-lg font-bold text-green-400">Claim Processing Use Case</h4>
        </div>
        <p className="text-sm text-gray-300 leading-relaxed">
          Insurance employees can directly invoke agent capabilities in Outlook to connect with iComposer,
          complete medical bill matching, and query customer policies and products to determine preliminary claim decisions.
        </p>
      </div>

      {/* Workflow Steps */}
      <div className="space-y-4">
        {useCaseWorkflow.map((step, index) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="relative"
            >
              {/* Step Number */}
              <div className="absolute -left-3 top-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow-lg z-10">
                {step.step}
              </div>

              <div className="bg-white/5 rounded-xl border border-green-500/20 p-5 ml-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="bg-green-500/20 p-2 rounded-lg">
                    <Icon className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-bold text-white mb-1">{step.title}</h5>
                    <p className="text-xs text-gray-400">{step.description}</p>
                  </div>
                </div>

                <ul className="space-y-1 ml-10">
                  {step.details.map((detail, idx) => (
                    <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                      <span className="text-green-400 mt-0.5">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Arrow */}
              {index < useCaseWorkflow.length - 1 && (
                <div className="flex justify-center py-2">
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Use Case Diagram */}
      <div className="bg-white/5 rounded-xl p-6 border border-green-500/30">
        <h4 className="text-lg font-bold text-green-400 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5" />
          System Integration Architecture
        </h4>
        <div className="relative aspect-video bg-gray-900/50 rounded-lg overflow-hidden cursor-pointer"
             onClick={() => setSelectedImage({ src: '/pic/email_assistant/use_case.webp', title: 'Claim Processing Use Case' })}>
          <img
            src="/pic/email_assistant/use_case.webp"
            alt="Use Case Architecture"
            className="w-full h-full object-contain"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity">
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <p className="text-white text-sm font-semibold">Click to enlarge</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          Outlook → Email Assistant → MCP → iComposer → Policy Systems
        </p>
      </div>

      {/* Business Impact */}
      <div className="bg-white/5 rounded-xl p-6 border border-blue-500/30">
        <h4 className="text-lg font-bold text-blue-400 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Quantified Business Impact
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {businessImpact.map((impact, index) => {
            const Icon = impact.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-gradient-to-br ${impact.color} rounded-lg p-4 relative overflow-hidden`}
              >
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative">
                  <Icon className="w-6 h-6 text-white/80 mb-2" />
                  <div className="text-lg font-bold text-white mb-1">{impact.title}</div>
                  <p className="text-xs text-white/90">{impact.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-xl border border-green-500/30 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-r from-green-500 to-teal-500 p-3 rounded-lg">
          <Mail className="w-8 h-8 text-white" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-green-400">Email Assistant</h3>
          <p className="text-sm text-gray-400">AI-Powered Insurance Email Processing</p>
        </div>
      </div>

      {/* Introduction */}
      <div className="mb-6 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
        <p className="text-gray-300 leading-relaxed">
          Evolution from <span className="text-orange-400 font-bold">simple LLM responses</span> to
          <span className="text-blue-400 font-bold"> RAG-enhanced agents</span> to
          <span className="text-green-400 font-bold"> production MCP integration</span>, enabling real claim processing directly in Outlook.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="flex gap-2 bg-gray-900/50 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('evolution')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
              activeTab === 'evolution'
                ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                : 'text-gray-400 hover:bg-white/5'
            }`}
          >
            <Layers className="w-4 h-4 inline mr-2" />
            Evolution
          </button>
          <button
            onClick={() => setActiveTab('usecase')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
              activeTab === 'usecase'
                ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                : 'text-gray-400 hover:bg-white/5'
            }`}
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            Use Case
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {activeTab === 'evolution' ? renderEvolutionContent() : renderUseCaseContent()}
      </div>

      {/* Evolution Image Modal */}
      {activeTab === 'evolution' && (
        <div className="mt-6 bg-white/5 rounded-xl p-6 border border-green-500/30">
          <h4 className="text-lg font-bold text-green-400 mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Platform Evolution
          </h4>
          <div className="relative aspect-video bg-gray-900/50 rounded-lg overflow-hidden cursor-pointer"
               onClick={() => setSelectedImage({ src: '/pic/email_assistant/evolution.webp', title: 'Email Assistant Evolution' })}>
            <img
              src="/pic/email_assistant/evolution.webp"
              alt="Evolution Diagram"
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity">
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-white text-sm font-semibold">Click to enlarge evolution diagram</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            Simple LLM → RAG + Agent → MCP Integration
          </p>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedImage(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative max-w-6xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-12 right-0 text-white hover:text-green-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <span className="text-2xl">✕</span>
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-center text-white mt-4">{selectedImage.title}</p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default EmailAssistantProject;
